import { Suspense, lazy, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { LogOut, Shield, Coins, Bug, Loader2, Menu } from "lucide-react";
import logo from "@/assets/logo.png";
import { useNavigate } from "react-router-dom";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";

const PaymentHistory = lazy(() => import("./PaymentHistory").then((m) => ({ default: m.PaymentHistory })));

interface HeaderProps {
  onUpgrade: () => void;
  isUpgrading?: boolean;
}

export function Header({ onUpgrade, isUpgrading = false }: HeaderProps) {
  const { user, profile, isAdmin, signOut } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSignOut = async () => {
    setMobileMenuOpen(false);
    await signOut();
    navigate("/");
  };

  return (
    <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
      <div className="container mx-auto px-4 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2 overflow-visible">
          <img src={logo} alt="Scalemage logo" className="h-[52px] w-[52px] object-contain" style={{ margin: '-0.5rem 0' }} />
          <span className="text-xl font-bold tracking-tight">Scalemage</span>
        </div>

        <div className="flex items-center gap-4">
          {user ? (
            <>
              {/* Desktop Layout */}
              {!isMobile && (
                <>
                  <button
                    onClick={() => setLanguage(language === "en" ? "es" : "en")}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors px-2"
                  >
                    {language === "en" ? "ES" : "EN"}
                  </button>

                  <Suspense fallback={<Skeleton className="h-8 w-8 rounded-md" />}>
                    <PaymentHistory />
                  </Suspense>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" asChild>
                        <a href="mailto:noster91@gmail.com?subject=Scalemage Issue Report">
                          <Bug className="h-4 w-4" />
                        </a>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t("header", "reportIssue")}</TooltipContent>
                  </Tooltip>

                  <div className="flex items-center gap-2 bg-secondary px-3 py-1.5 rounded-full">
                    <Coins className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium">
                      {profile?.credits ?? 0} {t("header", "credits")}
                    </span>
                  </div>

                  <Button
                    onClick={onUpgrade}
                    disabled={isUpgrading}
                    className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
                  >
                    {isUpgrading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {t("header", "loading")}
                      </>
                    ) : (
                      t("header", "upgrade")
                    )}
                  </Button>

                  {isAdmin && (
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => navigate("/admin")}
                      title={t("header", "adminDashboard")}
                    >
                      <Shield className="h-4 w-4" />
                    </Button>
                  )}

                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">{profile?.email}</span>
                    <Button variant="ghost" size="icon" onClick={handleSignOut} title={t("header", "signOut")}>
                      <LogOut className="h-4 w-4" />
                    </Button>
                  </div>
                </>
              )}

              {/* Mobile Layout */}
              {isMobile && (
                <>
                  <Button
                    onClick={onUpgrade}
                    disabled={isUpgrading}
                    size="sm"
                    className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
                  >
                    {isUpgrading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("header", "upgrade")}
                  </Button>

                  <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                    <SheetTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <Menu className="h-5 w-5" />
                      </Button>
                    </SheetTrigger>
                    <SheetContent side="right" className="w-72">
                      <SheetHeader>
                        <SheetTitle>{t("header", "menu")}</SheetTitle>
                      </SheetHeader>
                      <div className="flex flex-col gap-4 mt-6">
                        <button
                          onClick={() => setLanguage(language === "en" ? "es" : "en")}
                          className="text-sm text-muted-foreground hover:text-foreground transition-colors text-left px-1"
                        >
                          {language === "en" ? "Español" : "English"}
                        </button>

                        <div className="flex items-center gap-2 bg-secondary px-3 py-2 rounded-lg">
                          <Coins className="h-4 w-4 text-primary" />
                          <span className="text-sm font-medium">
                            {profile?.credits ?? 0} {t("header", "credits")}
                          </span>
                        </div>

                        <Suspense fallback={<Skeleton className="h-10 w-full rounded-md" />}>
                          <PaymentHistory />
                        </Suspense>

                        <Button variant="ghost" className="justify-start" asChild>
                          <a href="mailto:noster91@gmail.com?subject=Scalemage Issue Report">
                            <Bug className="h-4 w-4 mr-2" />
                            {t("header", "reportIssue")}
                          </a>
                        </Button>

                        {isAdmin && (
                          <Button
                            variant="ghost"
                            className="justify-start"
                            onClick={() => {
                              setMobileMenuOpen(false);
                              navigate("/admin");
                            }}
                          >
                            <Shield className="h-4 w-4 mr-2" />
                            {t("header", "adminDashboard")}
                          </Button>
                        )}

                        <div className="border-t border-border pt-4 mt-2">
                          <p className="text-sm text-muted-foreground mb-3 px-1">{profile?.email}</p>
                          <Button variant="ghost" className="justify-start w-full" onClick={handleSignOut}>
                            <LogOut className="h-4 w-4 mr-2" />
                            {t("header", "signOut")}
                          </Button>
                        </div>
                      </div>
                    </SheetContent>
                  </Sheet>
                </>
              )}
            </>
          ) : (
            <Button onClick={() => navigate("/auth")}>{t("landing", "signIn")}</Button>
          )}
        </div>
      </div>
    </header>
  );
}
